import React, { useEffect, useState } from 'react';
import { escapeHtml } from '@/utils/htmlEscape';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format } from 'date-fns';
import { Plus, ChevronDown, Search, Copy, Eye, Printer, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { useRosters } from '@/hooks/useRosters';
import { useClients } from '@/hooks/useClients';
import { useSites } from '@/hooks/useSites';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { mockRosters, mockSites, mockSiteClients } from '@/data/mockData';

interface RosterManagementProps {
  isDemo?: boolean;
  facilityId?: string; // When provided, pre-filter to this facility
}

const RosterManagement = ({ isDemo = false, facilityId }: RosterManagementProps) => {
  const { rosters: dbRosters, createRoster, updateRosterEntries, deleteRoster } = useRosters();
  const { clients: dbClients } = useClients();
  const { sites: dbSites } = useSites();
  
  // Use mock data in demo mode
  const rosters = isDemo ? mockRosters : dbRosters;
  const clients = isDemo ? Object.values(mockSiteClients).flat() : dbClients;
  const sites = isDemo ? mockSites : dbSites;
  
  // Pre-set facility filter when facilityId is provided
  const [isActiveRostersOpen, setIsActiveRostersOpen] = useState(true);
  const [isCreateRosterOpen, setIsCreateRosterOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState<string>(facilityId || 'all');
  const [rosterName, setRosterName] = useState('');
  const [rosterDate, setRosterDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewingRoster, setViewingRoster] = useState<any>(null);
  const [filterSite, setFilterSite] = useState<string>(facilityId || 'all');
  const [listSort, setListSort] = useState<{ key: 'date' | 'name' | 'shift'; dir: 'asc' | 'desc' }>({ key: 'date', dir: 'desc' });
  const [entrySort, setEntrySort] = useState<{ key: 'room' | 'status' | 'name'; dir: 'asc' | 'desc' } | null>(null);

  const toggleListSort = (key: 'date' | 'name' | 'shift') => {
    setListSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  };
  const toggleEntrySort = (key: 'room' | 'status' | 'name') => {
    setEntrySort(prev => !prev || prev.key !== key ? { key, dir: 'asc' } : { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' });
  };
  const SortIcon = ({ active, dir }: { active: boolean; dir?: 'asc' | 'desc' }) =>
    !active ? <ArrowUpDown className="h-3.5 w-3.5 ml-1 inline opacity-50" />
      : dir === 'asc' ? <ArrowUp className="h-3.5 w-3.5 ml-1 inline" />
      : <ArrowDown className="h-3.5 w-3.5 ml-1 inline" />;
  
  // Determine if facility selector should be hidden
  const hideFacilitySelector = !!facilityId;

  const filteredClients = clients.filter((client: any) => {
    // If a facility is selected, include clients assigned to that facility.
    // Also include checked-in clients missing a facility assignment (site_id is null) so they can still be rostered.
    if (selectedSite !== 'all' && client.site_id && client.site_id !== selectedSite) return false;
    if (client.archived_at) return false;
    if (searchTerm) {
      const fullName = `${client.name || ''}`.toLowerCase();
      return fullName.includes(searchTerm.toLowerCase());
    }
    return true;
  });

  // Pre-select only checked-in clients when the create dialog opens or facility changes
  useEffect(() => {
    if (!isCreateRosterOpen) return;
    const preselected = clients
      .filter((client: any) => {
        if (selectedSite !== 'all' && client.site_id && client.site_id !== selectedSite) return false;
        if (client.archived_at) return false;
        return client.status === 'checked-in';
      })
      .map((c: any) => c.id);
    setSelectedClients(preselected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateRosterOpen, selectedSite]);

  const filteredRosters = [...rosters]
    .filter(roster => filterSite === 'all' || roster.site_id === filterSite)
    .sort((a: any, b: any) => {
      const dir = listSort.dir === 'asc' ? 1 : -1;
      if (listSort.key === 'date') return (new Date(a.roster_date).getTime() - new Date(b.roster_date).getTime()) * dir;
      if (listSort.key === 'name') return String(a.name || '').localeCompare(String(b.name || '')) * dir;
      if (listSort.key === 'shift') return String(a.shift_type || '').localeCompare(String(b.shift_type || '')) * dir;
      return 0;
    });

  const handleCreateRoster = () => {
    if (!rosterName || !rosterDate || selectedClients.length === 0) return;

    // Resolve site: explicit selection > facilityId prop > single site fallback
    const resolvedSiteId =
      (selectedSite !== 'all' ? selectedSite : undefined) ||
      facilityId ||
      (sites.length === 1 ? sites[0].id : undefined);

    createRoster({
      name: rosterName,
      rosterDate,
      siteId: resolvedSiteId,
      clientIds: selectedClients
    });

    setIsCreateRosterOpen(false);
    setRosterName('');
    setRosterDate(format(new Date(), 'yyyy-MM-dd'));
    setSelectedClients([]);
  };

  const handleToggleClient = (clientId: string) => {
    setSelectedClients(prev =>
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleSelectAll = () => {
    if (selectedClients.length === filteredClients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients.map((c: any) => c.id));
    }
  };

  const handleCopyToNextDay = (roster: any) => {
    const nextDay = new Date(roster.roster_date);
    nextDay.setDate(nextDay.getDate() + 1);
    
    const clientIds = roster.roster_entries?.map((e: any) => e.client_id) || [];
    
    createRoster({
      name: `${roster.name} - Next Day`,
      rosterDate: format(nextDay, 'yyyy-MM-dd'),
      siteId: roster.site_id,
      shiftType: roster.shift_type,
      clientIds
    });
  };

  const handlePrintRoster = (roster: any) => {
    const rosterSite = sites.find((s: any) => s.id === roster.site_id);
    const isFamilySite = rosterSite?.is_family_shelter === true;

    const printContent = `
      <html>
        <head>
          <title>${escapeHtml(roster.name)} - Roster</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { margin-bottom: 10px; }
            .meta { color: #666; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; }
          </style>
        </head>
        <body>
          <h1>${escapeHtml(roster.name)}</h1>
          <div class="meta">
            <p>Date: ${format(new Date(roster.roster_date), 'MMMM dd, yyyy')}</p>
            <p>Shift: ${escapeHtml(roster.shift_type || 'Not specified')}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Room</th>
                ${isFamilySite ? '<th>Family</th>' : ''}
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${roster.roster_entries?.map((entry: any, index: number) => {
                const hasPartner = !!entry.client?.partner_id;
                const children = entry.client?.children || [];
                const childrenCount = Array.isArray(children) ? children.length : 0;
                const isFamily = entry.client?.is_head_of_household || hasPartner || childrenCount > 0;
                const familySize = 1 + (hasPartner ? 1 : 0) + childrenCount;
                return `
                <tr>
                  <td>${index + 1}</td>
                  <td>${escapeHtml(entry.client?.first_name || '')} ${escapeHtml(entry.client?.last_name || '')}</td>
                  <td>${escapeHtml(entry.client?.current_room?.room_number || entry.room?.room_number || 'Unassigned')}</td>
                  ${isFamilySite ? `<td>${isFamily ? familySize + ' people' : '—'}</td>` : ''}
                  <td>${escapeHtml(entry.client?.status || 'Unknown')}</td>
                </tr>
              `}).join('') || ''}
            </tbody>
          </table>
          <p style="margin-top: 20px; color: #666;">Total Clients: ${roster.roster_entries?.length || 0}</p>
        </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
    }
  };

  return (
    <div className="space-y-6">
      {/* Active Rosters Section */}
      <Collapsible open={isActiveRostersOpen} onOpenChange={setIsActiveRostersOpen}>
        <Card>
          <CardHeader className="pb-4">
            <CollapsibleTrigger asChild>
              <div className="flex items-center justify-between cursor-pointer">
                <CardTitle className="text-xl">Active Rosters</CardTitle>
                <div className="flex items-center gap-3">
                  <Button onClick={(e) => { e.stopPropagation(); setIsCreateRosterOpen(true); }} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Roster
                  </Button>
                  <ChevronDown 
                    className={cn(
                      "h-5 w-5 transition-transform text-muted-foreground",
                      isActiveRostersOpen && "transform rotate-180"
                    )}
                  />
                </div>
              </div>
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="pt-0">
              {rosters.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <p>No rosters created yet</p>
                  <p className="text-sm mt-1">Click "Create Roster" to get started</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="relative flex-1 max-w-sm">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search rosters..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                    {!hideFacilitySelector && (
                      <div className="w-full sm:w-48">
                        <Select value={filterSite} onValueChange={setFilterSite}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Facility" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Facilities</SelectItem>
                            {sites.map(site => (
                              <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead className="font-semibold cursor-pointer select-none" onClick={() => toggleListSort('date')}>
                            Date<SortIcon active={listSort.key === 'date'} dir={listSort.dir} />
                          </TableHead>
                          <TableHead className="font-semibold cursor-pointer select-none" onClick={() => toggleListSort('name')}>
                            Name<SortIcon active={listSort.key === 'name'} dir={listSort.dir} />
                          </TableHead>
                          <TableHead className="font-semibold">Clients</TableHead>
                          <TableHead className="font-semibold cursor-pointer select-none" onClick={() => toggleListSort('shift')}>
                            Shift<SortIcon active={listSort.key === 'shift'} dir={listSort.dir} />
                          </TableHead>
                          <TableHead className="font-semibold text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredRosters.map(roster => (
                          <TableRow key={roster.id} className="hover:bg-muted/30">
                            <TableCell className="font-medium">
                              {format(new Date(roster.roster_date), 'MMM dd, yyyy')}
                            </TableCell>
                            <TableCell>{roster.name}</TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="font-normal">
                                {roster.roster_entries?.length || 0} clients
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{roster.shift_type || 'Day'}</Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1 justify-end">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setViewingRoster(roster)}
                                  title="View"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleCopyToNextDay(roster)}
                                  title="Copy to Next Day"
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handlePrintRoster(roster)}
                                  title="Print"
                                >
                                  <Printer className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Create Roster Dialog */}
      <Dialog open={isCreateRosterOpen} onOpenChange={setIsCreateRosterOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Roster</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Roster Name</Label>
                <Input
                  value={rosterName}
                  onChange={(e) => setRosterName(e.target.value)}
                  placeholder="e.g., Morning Roster"
                />
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={rosterDate}
                  onChange={(e) => setRosterDate(e.target.value)}
                />
              </div>
            </div>
            
            {!hideFacilitySelector && (
              <div className="space-y-2">
                <Label>Facility</Label>
                <Select value={selectedSite} onValueChange={setSelectedSite}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select facility" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Facilities</SelectItem>
                    {sites.map(site => (
                      <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Search Clients</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name..."
                  className="pl-9"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Select Clients ({selectedClients.length} selected)</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleSelectAll}
                >
                  {selectedClients.length === filteredClients.length && filteredClients.length > 0
                    ? 'Deselect All'
                    : 'Select All'}
                </Button>
              </div>
              <div className="border rounded-lg max-h-64 overflow-y-auto">
                {filteredClients.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground text-sm">
                    No checked-in clients found
                  </div>
                ) : (
                  filteredClients.map((client: any) => (
                    <div
                      key={client.id}
                      className="flex items-center space-x-3 p-3 hover:bg-muted/50 cursor-pointer border-b last:border-b-0"
                      onClick={() => handleToggleClient(client.id)}
                    >
                      <Checkbox
                        checked={selectedClients.includes(client.id)}
                        onCheckedChange={() => handleToggleClient(client.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">
                          {client.name || 'Unknown'}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Room: {client.room || 'Unassigned'}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setIsCreateRosterOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateRoster} disabled={!rosterName || selectedClients.length === 0}>
                Create Roster
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Roster Dialog */}
      <Dialog open={!!viewingRoster} onOpenChange={() => setViewingRoster(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{viewingRoster?.name}</DialogTitle>
          </DialogHeader>
          {viewingRoster && (() => {
            const rosterSite = sites.find((s: any) => s.id === viewingRoster.site_id);
            const rosterSiteIsFamily = rosterSite?.is_family_shelter === true;
            return (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm bg-muted/50 p-3 rounded-lg">
                <div>
                  <span className="font-medium text-muted-foreground">Date:</span>{' '}
                  <span className="font-medium">{format(new Date(viewingRoster.roster_date), 'MMMM dd, yyyy')}</span>
                </div>
                <div>
                  <span className="font-medium text-muted-foreground">Shift:</span>{' '}
                  <span className="font-medium">{viewingRoster.shift_type || 'Day'}</span>
                </div>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="font-semibold w-12">#</TableHead>
                      <TableHead className="font-semibold cursor-pointer select-none" onClick={() => toggleEntrySort('name')}>
                        Name<SortIcon active={entrySort?.key === 'name'} dir={entrySort?.dir} />
                      </TableHead>
                      <TableHead className="font-semibold cursor-pointer select-none" onClick={() => toggleEntrySort('room')}>
                        Room<SortIcon active={entrySort?.key === 'room'} dir={entrySort?.dir} />
                      </TableHead>
                      {rosterSiteIsFamily && (
                        <TableHead className="font-semibold">Family</TableHead>
                      )}
                      <TableHead className="font-semibold cursor-pointer select-none" onClick={() => toggleEntrySort('status')}>
                        Status<SortIcon active={entrySort?.key === 'status'} dir={entrySort?.dir} />
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const entries = [...(viewingRoster.roster_entries || [])];
                      if (entrySort) {
                        const dir = entrySort.dir === 'asc' ? 1 : -1;
                        entries.sort((a: any, b: any) => {
                          if (entrySort.key === 'room') {
                            const ra = a.client?.current_room?.room_number || a.room?.room_number || '';
                            const rb = b.client?.current_room?.room_number || b.room?.room_number || '';
                            return String(ra).localeCompare(String(rb), undefined, { numeric: true }) * dir;
                          }
                          if (entrySort.key === 'status') {
                            return String(a.client?.status || '').localeCompare(String(b.client?.status || '')) * dir;
                          }
                          if (entrySort.key === 'name') {
                            const na = `${a.client?.first_name || ''} ${a.client?.last_name || ''}`.trim();
                            const nb = `${b.client?.first_name || ''} ${b.client?.last_name || ''}`.trim();
                            return na.localeCompare(nb) * dir;
                          }
                          return 0;
                        });
                      }
                      return entries;
                    })().map((entry: any, index: number) => {
                      const hasPartner = !!entry.client?.partner_id;
                      const children = entry.client?.children || [];
                      const childrenCount = Array.isArray(children) ? children.length : 0;
                      const isFamily = entry.client?.is_head_of_household || hasPartner || childrenCount > 0;
                      const familySize = 1 + (hasPartner ? 1 : 0) + childrenCount;
                      
                      return (
                        <TableRow key={entry.id}>
                          <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                className="text-primary hover:underline underline-offset-2 focus:outline-none cursor-pointer text-left"
                                onClick={() => {
                                  console.log('Open client profile:', entry.client?.id);
                                }}
                              >
                                {entry.client?.first_name} {entry.client?.last_name}
                              </button>
                              {rosterSiteIsFamily && isFamily && (
                                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                  Family
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {entry.client?.current_room?.room_number || entry.room?.room_number || 'Unassigned'}
                          </TableCell>
                          {rosterSiteIsFamily && (
                            <TableCell>
                              {isFamily ? (
                                <div className="text-sm">
                                  <span className="font-medium">{familySize} people</span>
                                  <span className="text-muted-foreground ml-1">
                                    ({hasPartner ? '+Partner' : ''}{hasPartner && childrenCount > 0 ? ', ' : ''}{childrenCount > 0 ? `${childrenCount} child${childrenCount > 1 ? 'ren' : ''}` : ''})
                                  </span>
                                </div>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                          )}
                          <TableCell>
                            <Badge className={
                              entry.client?.status === 'checked-in' 
                                ? 'bg-[hsl(var(--status-checked-in))] text-[hsl(var(--status-checked-in-foreground))]' 
                                : entry.client?.status === 'checked-out'
                                  ? 'bg-[hsl(var(--status-checked-out))] text-[hsl(var(--status-checked-out-foreground))]'
                                  : 'bg-muted text-muted-foreground'
                            }>
                              {entry.client?.status || 'Unknown'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <div className="flex justify-between items-center pt-2">
                <span className="text-sm text-muted-foreground">
                  Total: {viewingRoster.roster_entries?.length || 0} clients
                </span>
                <Button variant="outline" size="sm" onClick={() => handlePrintRoster(viewingRoster)}>
                  <Printer className="h-4 w-4 mr-2" />
                  Print
                </Button>
              </div>
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RosterManagement;
